/**
 * Represents resources from the /entry-passes/ API collection.
 */
import type { ActivatedRoute } from '@angular/router';
import { loadIntFilterFromGetParams, loadStringFilterFromGetParams } from '../reporting-core/utils';
import {
  CustomerEntryPassStatus, EntryPassDiscountType, EntryPassGiftDiscountType, EntryPassStatus,
  EntryPassVisibility,
} from './entry-pass-enums';
import { MembershipDiscountType } from './membership-enums';
import { GroupMembershipDiscount } from './membership';
import { PaymentMethodType, PaymentTypeWithMixed } from './payment-enums';

export interface EntryPassLocationSpecificPricingMatrix {
  location_ids: number[];
  price: string;
}

export interface EntryPass {
  id: number;
  url: string;
  name: string;
  status: EntryPassStatus;
  description: string;
  confirmation_text: string;
  payment_information_text: string;
  terms_and_conditions_checkbox_text: string;
  visibility: EntryPassVisibility;
  num_passes: number;
  price: number;
  location_specific_pricing_matrices: EntryPassLocationSpecificPricingMatrix[];
  tax_rate_id: number;
  tax_rate_name: string;
  tax_rate_per_location: { [key: string]: number };
  expiration_months: number;
  expiration_days: number;
  revenue_account_codes: { [key: string]: string | null };
  allow_purchasing_for_unlinked_customers: boolean;
  allow_purchase_without_login: boolean;
  has_limited_entry: boolean;
  mailchimp_last_entry_used_event_name: string;
  mailchimp_unused_passes_tag_name: string;
  mailchimp_purchased_by_customer_event_name: string;
  mailchimp_purchased_for_customer_event_name: string;
  order_number: number;
  created_at: string;
  updated_at: string;
  last_updated_by_id: number | null;
  last_updated_by_first_name: string | null;
  last_updated_by_last_name: string | null;
  last_updated_by_email: string | null;
  entry_time_slots_url: string;
}

export interface EntryPassEntryTimeSlot {
  id: number;
  url: string;
  start_time: string;
  end_time: string;
  is_available_sunday: boolean;
  is_available_monday: boolean;
  is_available_tuesday: boolean;
  is_available_wednesday: boolean;
  is_available_thursday: boolean;
  is_available_friday: boolean;
  is_available_saturday: boolean;
  created_at: string;
  updated_at: string;
}

export interface EntryPassDiscount {
  id: number;
  url: string;
  is_automatic: boolean;
  code: string;
  name: string;
  discount_type: EntryPassDiscountType;
  amount: number;
  start_date?: string;
  end_date?: string;
  entry_pass_ids: number[];
  entry_pass_names: string[];
  requires_association: boolean;
  requires_all_associations: boolean;
  association_ids: number[];
  association_names: string[];
  requires_membership: boolean;
  required_membership_ids: number[];
  required_membership_names: string[];
  max_number_of_uses_per_customer: number | null;
  required_no_check_in_period_days: number | null;
  min_check_in_count: number | null;
  max_check_in_count: number | null;
  min_number_of_days_without_membership: number | null;
  min_age: number | null;
  max_age: number | null;
  created_at: string;
  updated_at: string;
  last_updated_by_id: number | null;
  last_updated_by_first_name: string | null;
  last_updated_by_last_name: string | null;
  last_updated_by_email: string | null;
}

export interface EntryPassGiftingConfig {
  id: number;
  url: string;
  assigned_entry_pass_ids: number[];
  assigned_entry_pass_names: string[];
  max_future_delivery_date_days: number;
  expiration_days: number;
  gift_giver_terms_and_conditions: string;
  gift_recipient_terms_and_conditions: string;
  created_at: string;
  updated_at: string;
}

export interface EntryPassGiftDiscount {
  id: number;
  url: string;
  is_automatic: boolean;
  code: string;
  name: string;
  discount_type: EntryPassGiftDiscountType;
  amount: number;
  start_date?: string;
  end_date?: string;
  entry_pass_ids: number[];
  entry_pass_names: string[];
  created_at: string;
  updated_at: string;
  last_updated_by_id: number | null;
  last_updated_by_first_name: string | null;
  last_updated_by_last_name: string | null;
  last_updated_by_email: string | null;
}

export interface CustomerEntryPassCustomer {
  id: number;
  first_name: string;
  last_name: string;
}

export interface CustomerEntryPassUnlinkedCustomer {
  id: number;
  first_name: string;
  last_name: string;
}

export interface CustomerEntryPass {
  id: number;
  url: string;
  entry_pass_id: number;
  entry_pass_status: EntryPassStatus;
  name: string;
  created_date: string;
  status: CustomerEntryPassStatus;
  number_of_uses_total: number;
  number_of_uses_remaining: number;
  expiration_date?: string;
  fee_amount: number;
  invoice_id: number;
  invoice_amount: number;
  invoice_tax_amount: number;
  invoice_refunded_amount: number;
  invoice_payment_type?: PaymentTypeWithMixed;
  invoice_payment_method_type?: PaymentMethodType;
  invoice_location_id: number | null;
  invoice_location_name: string | null;
  owner_id: number;
  owner_url: string;
  owner_first_name: string;
  owner_last_name: string;
  owner_birthday: string;
  owner_email: string;
  owner_is_member: boolean;
  owner_association_id: number | null;
  owner_association_url: string | null;
  owner_association_name: string | null;
  owner_association_allow_automatic_entry_pass_use: boolean | null;
  all_customer_ids: number[];
  all_customers: CustomerEntryPassCustomer[];
  customers_less_owner: CustomerEntryPassCustomer[];
  unlinked_customers: CustomerEntryPassUnlinkedCustomer[];
  created_at: string;
  updated_at: string;
}

export interface EntryPassFilters {
  status?: EntryPassStatus;
}

export interface EntryPassDiscountFilters {
  search?: string;
  association_id?: number;
}

export interface EntryPassGiftDiscountFilters {
  search?: string;
}

export interface CustomerEntryPassFilters {
  created_at_min?: string;
  created_at_max?: string;
  created_at_range?: string;
  status_in?: string;
  entry_pass_status_in?: string;
  number_of_uses_remaining_min?: number;
  number_of_uses_remaining_max?: number;
  owner_association_id?: number;
  location_name_contains?: string;
  invoice_location_id?: number;
}

export function loadEntryPassDiscountFiltersFromGetParams(activatedRoute: ActivatedRoute): EntryPassDiscountFilters {
  const filters: EntryPassDiscountFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadStringFilterFromGetParams(filters, 'search', initialQPs);

  return filters;
}

export function loadCustomerEntryPassFiltersFromGetParams(activatedRoute: ActivatedRoute): CustomerEntryPassFilters {
  const filters: CustomerEntryPassFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadStringFilterFromGetParams(filters, 'created_at_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'created_at_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'created_at_range', initialQPs);
  loadStringFilterFromGetParams(filters, 'status_in', initialQPs);
  loadStringFilterFromGetParams(filters, 'entry_pass_status_in', initialQPs);
  loadIntFilterFromGetParams(filters, 'number_of_uses_remaining_min', initialQPs);
  loadIntFilterFromGetParams(filters, 'number_of_uses_remaining_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'location_name_contains', initialQPs);
  loadIntFilterFromGetParams(filters, 'invoice_location_id', initialQPs);

  return filters;
}

export function getEntryPassStatusDisplay(status: EntryPassStatus) {
  if (status === EntryPassStatus.draft) {
    return 'Draft';
  } else if (status === EntryPassStatus.active) {
    return 'Active';
  } else if (status === EntryPassStatus.retired) {
    return 'Retired';
  } else {
    return 'Unknown';
  }
}

export function getCustomerEntryPassStatusDisplay(status: CustomerEntryPassStatus) {
  if (status === CustomerEntryPassStatus.awaitingPayment) {
    return 'Awaiting Payment';
  } else if (status === CustomerEntryPassStatus.active) {
    return 'Active';
  } else if (status === CustomerEntryPassStatus.depleted) {
    return 'Depleted';
  } else if (status === CustomerEntryPassStatus.expired) {
    return 'Expired';
  } else if (status === CustomerEntryPassStatus.removed) {
    return 'Removed';
  } else {
    return 'Unknown';
  }
}
